import "./App.css";
import { HashRouter as Router, Routes, Route, Navigate, Outlet } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import React, { useEffect } from "react";

// Components
import Dashboard from "./components/dashboard/dashboard";
import AssetListPage from "./components/asset-list/asset-list";
import AssetCreate from "./components/asset-list/asset-create";
import CheckingAsset from "./components/checking/asset-checking";
import CheckingView from "./components/checking/checking-view";
import AssetView from "./components/assets/asset-view";
import Report from "./components/report/report";
import TemplateList from "./components/masters/templates/template";
import TemplateAdd from "./components/masters/templates/template-add";
import Help from "./components/common/help";
import Unauthorized from "./components/common/unauthorized";
import Layout from "./components/Layout/Layout";

// Context & Utils
import { useGlobal } from "./GlobalContext";
import { getCookie } from "./components/getcookie";
import { getRolePermissionApi, getRoleTypeApi } from "./components/common/common-api";
import { apiCallAdmin } from "./service/api-service.js";

const ProtectedRoute = () => {
  const { userType } = useGlobal();
  return userType === "Admin" || userType === "Org-Admin" || userType === "HR" ? <Outlet /> : <Navigate to="/unauthorized" />;
};

const ProtectedRoleRoute = ({ isAllowed }) => {
  return isAllowed ? <Outlet /> : <Navigate to="/unauthorized" />;
};

const DefaultRoute = ({ checkPermission }) => {
  const getFirstAvailableRoute = () => {
    const menuRouteMap = [
      { menu: "Dashboard", route: "/dashboard" },
      { menu: "Asset", route: "/asset" },
      { menu: "Asset-Checking", route: "/checking" },
      { menu: "Report", route: "/report" },
      { menu: "Masters", route: "/masters/template" },
    ];

    for (const item of menuRouteMap) {
      if (checkPermission(item.menu, "menu_permission")) {
        return item.route;
      }
    }
    return "/unauthorized";
  };

  if (checkPermission("Dashboard", "menu_permission")) {
    return <Dashboard />;
  }
  const firstRoute = getFirstAvailableRoute();
  if (firstRoute === "/dashboard") {
    return <Dashboard />;
  }
  return <Navigate to={firstRoute} replace />;
};

function App() {
  const adminApiUrl = process.env.REACT_APP_ADMIN_API_URL;
  const environment = process.env.REACT_APP_ENVIRONMENT;
  const accountsUrl = process.env.REACT_APP_ACCOUNTS_URL;

  const { rolePermissionList, setRolePermissionList, userType, setUserType } = useGlobal();

  useEffect(() => {
    const channel = new BroadcastChannel(`auth_channel_mawalog_${environment}`);
    channel.onmessage = () => {
      window.location.reload();
    };
    return () => channel.close();
  }, [environment]);

  useEffect(() => {
    const checkTokenAndFetch = async () => {
      try {
        console.log("🔍 Checking token validity...");

        const response = await apiCallAdmin("get", "validatetoken");

        if (response.status === 200) {
          getRolePermission();
          getRoleType();
        } else {
          handleInvalidToken();
        }
      } catch (err) {
        console.error("Token validation failed:", err);
        handleInvalidToken();
      }
    };

    checkTokenAndFetch();
  }, [adminApiUrl]);

  const getRolePermission = async () => {
    try {
      const response = await getRolePermissionApi(4);
      if (response && response.status === 200 && response.data.success === true) {
        setRolePermissionList(response.data.role_permission ?? []);
        localStorage.setItem("rolePermissionList", JSON.stringify(response.data.role_permission ?? []));
      } else {
        setRolePermissionList([]);
      }
    } catch (err) {
      console.error("Error fetching role permissions:", err);
    }
  };

  const getRoleType = async () => {
    try {
      const response = await getRoleTypeApi();
      if (response && response.status === 200 && response.data.success === true) {
        setUserType(response.data.type);
        localStorage.setItem("userType", response.data.type ?? "");
      } else {
        setUserType("");
      }
    } catch (err) {
      console.error("Error fetching role type:", err);
    }
  };

  const handleInvalidToken = () => {
    const currentURL = window.location.href;
    const redirectData = {
      redirectPath: currentURL,
      app: "asset",
      ts: Date.now(),
    };
    window.name = JSON.stringify(redirectData);
    document.cookie = "mawalog=; path=/; domain=.mawalog.com; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    localStorage.removeItem("userType");
    localStorage.removeItem("rolePermissionList");
    setUserType("");
    setRolePermissionList([]);
    window.location.href = `${accountsUrl}/login`;
  };

  function checkPermission(menuName, permission) {
    return rolePermissionList.findIndex((element) => element["name"] === menuName && element["menu_permission"] === true && element[permission] === true) > -1;
  }

  return (
    <div className="App">
      <Router>
        <Routes>
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/help" element={<Help />} />

          <Route element={<Layout />}>
            <Route path="/" element={<DefaultRoute checkPermission={checkPermission} />} />
            <Route path="/dashboard" element={<DefaultRoute checkPermission={checkPermission} />} />

            {/* Assets */}
            <Route element={<ProtectedRoleRoute isAllowed={checkPermission("Asset", "menu_permission")} />}>
              <Route path="/asset" element={<AssetListPage />} />
            </Route>
            <Route element={<ProtectedRoleRoute isAllowed={checkPermission("Asset", "create_permission")} />}>
              <Route path="/asset/create" element={<AssetCreate />} />
              <Route path="/asset/edit/:id" element={<AssetCreate />} />
            </Route>
            <Route element={<ProtectedRoleRoute isAllowed={checkPermission("Asset", "view_permission")} />}>
              <Route path="/asset/view" element={<AssetView />} />
            </Route>

            {/* Asset-Checking */}
            <Route element={<ProtectedRoleRoute isAllowed={checkPermission("Asset-Checking", "menu_permission")} />}>
              <Route path="/checking" element={<CheckingAsset />} />
              <Route path="/checking-view" element={<CheckingView />} />
            </Route>

            {/* Reports */}
            <Route element={<ProtectedRoleRoute isAllowed={checkPermission("Report", "menu_permission")} />}>
              <Route path="/report" element={<Report />} />
            </Route>

            {/* Masters */}
            <Route element={<ProtectedRoute />}>
              <Route path="/masters/template" element={<TemplateList />} />
              <Route path="/masters/template/add" element={<TemplateAdd />} />
            </Route>
          </Route>
        </Routes>
      </Router>
    </div>
  );
}

export default App;
